import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, Plus, Pencil, Trash2, Upload, ExternalLink, Link as LinkIcon, Image as ImageIcon } from "lucide-react";
import AiImage from "../components/AiImage";
import DatePickerInput from "../components/DatePickerInput";
import { resolveImageUrl } from "../context/AppSettingsContext";

const EMPTY = {
  title: "", description: "", cover_url: "", pdf_url: "",
  external_link: "", published: true, valid_from: "", valid_until: "",
  branch_ids: [], notify_push: false,
};

// Resolve an absolute URL for anything the backend returns under /api/*
const abs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? u : `/${u}`}`;
};

export default function Flyers() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const pdfRef = useRef(null);
  const coverRef = useRef(null);

  const load = () => api.get("/flyers").then((r) => setRows(r.data)).catch((err) => toast.error(formatApiError(err)));
  const [autoPush, setAutoPush] = useState(false);
  useEffect(() => { api.get("/flyers/settings").then((r) => setAutoPush(!!r.data?.auto_push)).catch(() => {}); }, []);
  const saveAutoPush = (v) => { setAutoPush(v); api.put("/flyers/settings", { auto_push: v }).then(() => toast.success(v ? "Promemoria automatico attivo" : "Promemoria automatico disattivato")).catch((err) => { setAutoPush(!v); toast.error(formatApiError(err)); }); };
  useEffect(() => {
    load();
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (f) => {
    setEditing(f);
    setForm({
      title: f.title || "", description: f.description || "",
      cover_url: f.cover_url || "", pdf_url: f.pdf_url || "",
      external_link: f.external_link || "", published: f.published !== false,
      valid_from: (f.valid_from || "").slice(0, 10),
      valid_until: (f.valid_until || "").slice(0, 10),
      branch_ids: f.branch_ids || [],
      notify_push: false, // never re-notify on edit unless explicitly checked
    });
    setOpen(true);
  };

  const toggleBranch = (bid) => {
    setForm((p) => {
      const has = p.branch_ids.includes(bid);
      return { ...p, branch_ids: has ? p.branch_ids.filter((x) => x !== bid) : [...p.branch_ids, bid] };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titolo obbligatorio");
    if (!form.pdf_url && !form.external_link) return toast.error("Carica almeno un PDF o inserisci un link esterno");
    if (form.external_link && !/^https?:\/\//i.test(form.external_link)) {
      return toast.error("Il link deve iniziare con http:// o https://");
    }
    try {
      const res = editing
        ? await api.patch(`/flyers/${editing.id}`, form)
        : await api.post("/flyers", form);
      const sent = res.data?.push_sent || 0;
      toast.success(sent > 0
        ? `Volantino salvato · Push inviata a ${sent} clienti`
        : "Volantino salvato");
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo volantino?")) return;
    try { await api.delete(`/flyers/${id}`); toast.success("Eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const onUploadPdf = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.type !== "application/pdf") { toast.error("Solo PDF"); return; }
    setUploadingPdf(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, pdf_url: data.url }));
      toast.success("PDF caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingPdf(false); e.target.value = ""; }
  };

  const onUploadCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingCover(true);
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "flyer");
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, cover_url: data.thumb_url || data.url }));
      toast.success("Copertina caricata");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingCover(false); e.target.value = ""; }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="flyers-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><FileText className="h-3 w-3" /> VOLANTINI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Volantini & locandine</h1>
          <p className="text-zinc-400 mt-1">Pubblica un PDF o collega un sito esterno. I clienti li vedranno in "Volantino".</p>
        </div>
        <Button data-testid="flyer-new" onClick={openNew} className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11">
          <Plus className="h-4 w-4 mr-2" /> Nuovo volantino
        </Button>
      </div>

      <div className="card-elev p-4 flex items-center justify-between gap-3" data-testid="flyer-auto-push-card">
        <div>
          <div className="text-sm font-medium">📲 Promemoria volantino automatico</div>
          <p className="text-xs text-zinc-400 mt-0.5">Ogni volta che pubblichi un nuovo volantino, i clienti (della sede o di tutte le sedi) ricevono subito una notifica push. La casella «Notifica push» nel modulo resta per i reinvii manuali.</p>
        </div>
        <Switch data-testid="flyer-auto-push" checked={autoPush} onCheckedChange={saveAutoPush} />
      </div>

      <div className="card-elev overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="text-left">
                <th className="p-4">Cover</th><th className="p-4">Titolo</th><th className="p-4">PDF / Link</th>
                <th className="p-4">Validità</th><th className="p-4">Stato</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Nessun volantino pubblicato</td></tr>}
              {rows.map((f) => (
                <tr key={f.id} className="border-t border-zinc-900" data-testid={`flyer-row-${f.id}`}>
                  <td className="p-4">
                    {f.cover_url ? (
                      <img src={resolveImageUrl(f.cover_url)} alt="" className="h-14 w-14 object-cover rounded-md border border-zinc-800" />
                    ) : (
                      <div className="h-14 w-14 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center text-zinc-600">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="font-medium">{f.title}</div>
                    {f.description && <div className="text-xs text-zinc-500 line-clamp-1">{f.description}</div>}
                  </td>
                  <td className="p-4 space-y-1">
                    {f.pdf_url && (
                      <a href={abs(f.pdf_url)} target="_blank" rel="noopener noreferrer" className="text-amber-300 hover:text-amber-100 text-xs inline-flex items-center gap-1" data-testid={`flyer-pdf-${f.id}`}>
                        <FileText className="h-3 w-3" /> PDF
                      </a>
                    )}
                    {f.external_link && (
                      <a href={f.external_link} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-100 text-xs inline-flex items-center gap-1 block" data-testid={`flyer-link-${f.id}`}>
                        <ExternalLink className="h-3 w-3" /> Sito esterno
                      </a>
                    )}
                  </td>
                  <td className="p-4 text-xs text-zinc-500 whitespace-nowrap">
                    {f.valid_from ? f.valid_from.slice(0, 10) : "—"} → {f.valid_until ? f.valid_until.slice(0, 10) : "—"}
                  </td>
                  <td className="p-4">
                    <span className={`pill ${f.published ? "pill-mint" : "pill-muted"}`}>{f.published ? "Pubblicato" : "Bozza"}</span>
                  </td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`flyer-edit-${f.id}`} onClick={() => openEdit(f)} className="p-2 rounded-md text-zinc-500 hover:text-amber-300"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`flyer-delete-${f.id}`} onClick={() => remove(f.id)} className="p-2 rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica volantino" : "Nuovo volantino"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Titolo *</Label>
              <Input data-testid="flyer-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-zinc-950 border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Descrizione</Label>
              <Textarea data-testid="flyer-form-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-800" rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Valido dal</Label>
                <DatePickerInput testid="flyer-form-from" value={form.valid_from} max={form.valid_until || undefined} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </div>
              <div>
                <Label className="text-zinc-300 mb-1 inline-block">Valido al</Label>
                <DatePickerInput testid="flyer-form-to" value={form.valid_until} min={form.valid_from || undefined} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>

            {/* PDF upload */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 flex items-center gap-1"><FileText className="h-3 w-3" /> PDF Volantino</Label>
                {form.pdf_url && (
                  <a href={abs(form.pdf_url)} target="_blank" rel="noopener noreferrer" className="text-xs text-amber-300 hover:text-amber-100" data-testid="flyer-form-pdf-preview">Anteprima</a>
                )}
              </div>
              <div className="flex gap-2">
                <input ref={pdfRef} type="file" accept="application/pdf" className="hidden" data-testid="flyer-form-pdf-input" onChange={onUploadPdf} />
                <Button variant="outline" className="border-zinc-800 flex-1" onClick={() => pdfRef.current?.click()} disabled={uploadingPdf} data-testid="flyer-form-pdf-upload">
                  <Upload className="h-3 w-3 mr-1" /> {uploadingPdf ? "Caricamento..." : (form.pdf_url ? "Sostituisci PDF" : "Carica PDF")}
                </Button>
                {form.pdf_url && (
                  <Button variant="outline" className="border-red-500/40 text-red-300" onClick={() => setForm({ ...form, pdf_url: "" })}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-zinc-500">Max 10 MB · Solo PDF. Il cliente potrà aprirlo o scaricarlo dal portale.</p>
            </div>

            {/* External link */}
            <div>
              <Label className="text-zinc-300 mb-1 inline-block flex items-center gap-1"><LinkIcon className="h-3 w-3" /> Link esterno (opzionale)</Label>
              <Input data-testid="flyer-form-link" type="url" placeholder="https://…" value={form.external_link} onChange={(e) => setForm({ ...form, external_link: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              <p className="text-[11px] text-zinc-500 mt-1">Se compilato, sul volantino apparirà un bottone "Scopri di più" verso questo URL.</p>
            </div>

            {/* Cover image */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Copertina (opzionale)</Label>
                {form.cover_url && <img src={resolveImageUrl(form.cover_url)} alt="" className="h-10 w-10 rounded object-cover" />}
              </div>
              <input ref={coverRef} type="file" accept="image/*" className="hidden" data-testid="flyer-form-cover-input" onChange={onUploadCover} />
              <div className="flex gap-2">
                <Button variant="outline" className="border-zinc-800 flex-1" onClick={() => coverRef.current?.click()} disabled={uploadingCover} data-testid="flyer-form-cover-upload">
                  <Upload className="h-3 w-3 mr-1" /> {uploadingCover ? "Caricamento..." : (form.cover_url ? "Sostituisci" : "Carica copertina")}
                </Button>
                {form.cover_url && (
                  <Button variant="outline" className="border-red-500/40 text-red-300" onClick={() => setForm({ ...form, cover_url: "" })}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <AiImage
                triggerLabel="Genera copertina con AI"
                testid="flyer-ai-image"
                defaultPrompt={form.title ? `Copertina volantino promo "${form.title}". Layout minimal, colori vivaci, testo grande, mood ristorante/kebab.` : ""}
                promptHint="Es. Promo estiva -20% su tutti i kebab"
                onGenerated={(url) => setForm((p) => ({ ...p, cover_url: url }))}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div><div className="text-sm font-medium">Pubblicato</div><div className="text-xs text-zinc-500">Disattiva per salvare come bozza</div></div>
              <Switch data-testid="flyer-form-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>

            {/* Multi-branch selection: empty = visibile a tutte le sedi */}
            <div className="p-3 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2" data-testid="flyer-form-branches">
              <Label className="text-zinc-300 text-sm">Sedi visibili</Label>
              <p className="text-[11px] text-zinc-500 -mt-1">Se non selezioni nessuna sede il volantino sarà mostrato a <span className="text-amber-300">tutte le sedi</span>.</p>
              <div className="flex flex-wrap gap-2">
                {branches.length === 0 && <div className="text-xs text-zinc-500">Nessuna sede configurata.</div>}
                {branches.map((b) => {
                  const on = form.branch_ids.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`flyer-form-branch-${b.id}`}
                      onClick={() => toggleBranch(b.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${on ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}
                    >
                      {on ? "✓ " : ""}{b.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Push notification toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800">
              <div>
                <div className="text-sm font-medium">📲 Notifica push clienti</div>
                <div className="text-xs text-zinc-500">Invia una notifica web a tutti i clienti opt-in{form.branch_ids.length > 0 ? " delle sedi selezionate" : ""}.</div>
              </div>
              <Switch
                data-testid="flyer-form-notify"
                checked={!!form.notify_push}
                onCheckedChange={(v) => setForm({ ...form, notify_push: v })}
                disabled={!form.published}
              />
            </div>

            <Button data-testid="flyer-form-submit" onClick={submit} className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {editing ? "Salva modifiche" : "Pubblica volantino"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
